import { apiRequest } from './client';

// ------------------------- Settings ----------------------------------

export interface DocEntry {
  module: string;
  title: string;
  contentMd: string;
  gitCommit: string | null;
  updatedAt: string;
}

export interface SystemInfo {
  productName: string;
  environment: string;
  nodeVersion: string;
  gitCommit: string | null;
  llmProvider: string;
  llmModel: string;
  region: string;
  uptimeSeconds: number;
  featureFlags: Record<string, boolean>;
  docCount: number;
  renderService: string | null;
}

export const settingsApi = {
  listDocs: () => apiRequest<DocEntry[]>('/api/settings/docs'),
  getDoc: (moduleName: string) =>
    apiRequest<DocEntry>(`/api/settings/docs/${encodeURIComponent(moduleName)}`),
  systemInfo: () => apiRequest<SystemInfo>('/api/settings/system-info'),
  resyncDocs: () =>
    apiRequest<{ resynced: true }>('/api/settings/docs/resync', { method: 'POST' }),
};

// ------------------------- Masters -----------------------------------

export interface MasterTypeSummary {
  key: string;
  name: string;
  description: string;
  isSystem: boolean;
  recordCount: number;
  updatedAt: string;
}

export interface MasterRecordSummary {
  id: string;
  recordKey: string;
  /** Full record content — needed to group rows by a field inside it. */
  content: Record<string, unknown>;
  version: number;
  isActive: boolean;
  updatedAt: string;
  createdBy: string;
}

export interface MasterTypeDetail extends MasterTypeSummary {
  schemaJson: Record<string, unknown>;
  records: MasterRecordSummary[];
}

export interface MasterHistoryEntry {
  version: number;
  changedBy: string;
  changeReason: string | null;
  createdAt: string;
}

export interface MasterRecordDetail {
  id: string;
  masterTypeKey: string;
  masterTypeName: string;
  recordKey: string;
  content: Record<string, unknown>;
  version: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  history: MasterHistoryEntry[];
}

export const mastersApi = {
  listTypes: () => apiRequest<MasterTypeSummary[]>('/api/masters/types'),
  getType: (key: string) =>
    apiRequest<MasterTypeDetail>(`/api/masters/types/${encodeURIComponent(key)}`),
  getRecord: (id: string) => apiRequest<MasterRecordDetail>(`/api/masters/records/${id}`),

  // --- write ---
  createType: (body: {
    key: string;
    name: string;
    description: string;
    schemaJson: Record<string, unknown>;
  }) => apiRequest<{ id: string; key: string }>('/api/masters/types', { method: 'POST', body }),

  upsertRecord: (body: {
    masterTypeKey: string;
    recordKey: string;
    content: Record<string, unknown>;
    changeReason?: string;
  }) =>
    apiRequest<{ id: string; version: number }>('/api/masters/records', {
      method: 'POST',
      body,
    }),

  updateRecord: (
    id: string,
    body: {
      content: Record<string, unknown>;
      isActive?: boolean;
      changeReason?: string;
    },
  ) =>
    apiRequest<{ id: string; version: number }>(`/api/masters/records/${id}`, {
      method: 'PATCH',
      body,
    }),

  rollbackRecord: (
    id: string,
    body: { targetVersion: number; changeReason: string },
  ) =>
    apiRequest<{ rolledBack: true }>(`/api/masters/records/${id}/rollback`, {
      method: 'POST',
      body,
    }),

  importRecords: (body: {
    masterTypeKey: string;
    records: Array<{ recordKey: string; content: Record<string, unknown> }>;
    changeReason?: string;
  }) =>
    apiRequest<{
      total: number;
      succeeded: number;
      results: Array<{ recordKey: string; ok: boolean; error?: string }>;
    }>('/api/masters/import', { method: 'POST', body }),

  /** Import one product's approval ladder from an uploaded .xlsx / .csv sheet. */
  importApproverSheet: (body: {
    productCode: string;
    fileName: string;
    contentBase64: string;
    changeReason?: string;
  }) =>
    apiRequest<ApproverSheetImportResult>('/api/masters/deviation-approvers/import', {
      method: 'POST',
      body,
    }),
};

export interface ApproverSheetImportResult {
  productCode: string;
  fileName: string;
  levelsImported: number[];
  levelsUntouched: number[];
  approverIdCount: number;
  skipped: Array<{ row: number; why: string }>;
  columnsUsed: { level: string; role: string | null; approvers: string[] };
}

// ------------------------- Admin -------------------------------------

export interface AdminSystemInfo extends SystemInfo {
  activeUsers: number;
  activeSessions: number;
  auditLogEntries: number;
}

export interface AuditLogEntry {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  details: Record<string, unknown>;
  ipAddress: string | null;
  createdAt: string;
}

export interface AuditLogQuery {
  page: number;
  pageSize: number;
  actorEmail?: string;
  action?: string;
  targetType?: string;
  targetId?: string;
  from?: string; // ISO
  to?: string;   // ISO
}

export interface ConfigEntry {
  key: string;
  value: unknown;
  description: string;
  updatedBy: string | null;
  updatedAt: string;
}

export const adminApi = {
  systemInfo: () => apiRequest<AdminSystemInfo>('/api/admin/system-info'),
  auditLog: (q: AuditLogQuery) =>
    apiRequest<{
      items: AuditLogEntry[];
      total: number;
      page: number;
      pageSize: number;
    }>('/api/admin/audit-log', {
      query: q as unknown as Record<string, string | number | boolean | undefined>,
    }),
  auditActions: () => apiRequest<string[]>('/api/admin/audit-actions'),

  listConfig: () => apiRequest<ConfigEntry[]>('/api/admin/config'),
  upsertConfig: (body: { key: string; valueJson: unknown; description: string }) =>
    apiRequest<ConfigEntry>('/api/admin/config', { method: 'POST', body }),
  deleteConfig: (key: string) =>
    apiRequest<{ deleted: true }>(`/api/admin/config/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),
};
